const accounts = (count) => `${count} ${count === 1 ? 'account' : 'accounts'}`;

const MONARCH_ACCOUNTS_LISTED = 5;

/**
 * A Monarch account's name for reading: "Brokerage - ****8836 (...8836)" says its digits twice, so the
 * trailing "(...8836)" goes when the name already ends with them. The name itself, the key a map is
 * saved under, is untouched.
 */
export function monarchAccountName(name) {
	const match = /^(.*?(\d{4}))\s*\(\.\.\.(\d{4})\)$/.exec(name || '');
	return match && match[2] === match[3] ? match[1] : name;
}

/**
 * What a fetch from Monarch did to the balances (Import_Domain::fetch_monarch's `balances`, the
 * summary Monarch_Api_Sync::record_balances() returns), as a line for the import page and an error
 * line of its own. Null for either when there is nothing to say.
 *
 * @return {{ line: string|null, error: string|null }}
 */
export default function monarchBalanceSummary(balances, { brief = false } = {}) {
	if(!balances) {
		return { line: null, error: null };
	}
	if(balances.error) {
		return { line: null, error: `Balances were not updated: ${balances.error}` };
	}

	let line = `Balances updated for ${accounts(balances.written || 0)}`;
	const unmapped = balances.unmapped || [];
	if(unmapped.length && brief) {
		line += `; ${unmapped.length} Monarch ${unmapped.length === 1 ? 'account' : 'accounts'} not mapped`;
	} else if(unmapped.length) {
		const names = unmapped.slice(0, MONARCH_ACCOUNTS_LISTED).map(monarchAccountName).join(', ');
		const more = unmapped.length > MONARCH_ACCOUNTS_LISTED ? `, and ${unmapped.length - MONARCH_ACCOUNTS_LISTED} more` : '';
		line += `; ${unmapped.length} Monarch ${unmapped.length === 1 ? 'account' : 'accounts'} not mapped: ${names}${more}`;
	}
	const skipped = (balances.skipped || []).length;
	if(skipped) {
		line += `; ${skipped} mapped to be skipped`;
	}
	const bankReported = (balances.bank_reported || []).length;
	if(bankReported) {
		line += `; ${bankReported} kept the bank's own reading for the day`;
	}
	const closed = (balances.closed || []).length;
	if(closed) {
		line += `; ${closed} closed in Monarch`;
	}
	const ambiguous = (balances.ambiguous || []).map(monarchAccountName);
	if(ambiguous.length && brief) {
		line += ambiguous.length === 1
			? '; 1 name shared by more than one Monarch account'
			: `; ${ambiguous.length} names shared by more than one Monarch account`;
	} else if(ambiguous.length) {
		line += ambiguous.length === 1
			? `; skipped ${ambiguous[0]}, a name more than one Monarch account shares`
			: `; skipped ${ambiguous.join(', ')}, names more than one Monarch account shares`;
	}
	return { line: `${line}.`, error: null };
}
